// Audio I/O device enumeration helpers (spec §2.8, Phase 3 / U-DEV-1〜3).
//
// Browser APIs used:
//   navigator.mediaDevices.enumerateDevices()
//   navigator.mediaDevices.addEventListener("devicechange", ...)
//   HTMLMediaElement.prototype.setSinkId   — absent on iOS Safari
//
// `label` is only populated after the user has granted microphone permission
// at least once (per-origin). Before that, the list still contains device
// entries but the names are empty strings. Consumers should treat empty
// labels as "unnamed device — use the deviceId only".

export interface AudioDeviceInfo {
  deviceId: string;
  label: string;
  kind: "audioinput" | "audiooutput";
  /** True when we heuristically believe this is a Bluetooth / wireless
   *  device. Derived from the label — inaccurate by design. */
  isBluetooth: boolean;
}

/** Regex used to tag wireless peripherals. Case-insensitive; we accept
 *  false positives because the label is the only signal the browser gives
 *  us and getting it exactly right is not possible in every browser. */
const BLUETOOTH_LABEL = /(bluetooth|bt|airpods|wireless|headphones?|earbuds)/i;

export function supportsSinkId(): boolean {
  return (
    typeof HTMLMediaElement !== "undefined" &&
    "setSinkId" in HTMLMediaElement.prototype
  );
}

/** Enumerate audio input and output devices. Returns grouped lists.
 *  Labels may be empty until the user has permitted microphone access
 *  at least once in this origin. */
export async function enumerateAudioDevices(): Promise<{
  inputs: AudioDeviceInfo[];
  outputs: AudioDeviceInfo[];
}> {
  if (
    typeof navigator === "undefined" ||
    !navigator.mediaDevices ||
    typeof navigator.mediaDevices.enumerateDevices !== "function"
  ) {
    return { inputs: [], outputs: [] };
  }
  const list = await navigator.mediaDevices.enumerateDevices();
  const inputs: AudioDeviceInfo[] = [];
  const outputs: AudioDeviceInfo[] = [];
  for (const d of list) {
    if (d.kind !== "audioinput" && d.kind !== "audiooutput") continue;
    const entry: AudioDeviceInfo = {
      deviceId: d.deviceId,
      label: d.label ?? "",
      kind: d.kind,
      isBluetooth: BLUETOOTH_LABEL.test(d.label ?? ""),
    };
    (d.kind === "audioinput" ? inputs : outputs).push(entry);
  }
  return { inputs, outputs };
}

/** Subscribe to devicechange; returns an unsubscribe thunk. */
export function onDeviceChange(handler: () => void): () => void {
  if (
    typeof navigator === "undefined" ||
    !navigator.mediaDevices ||
    typeof navigator.mediaDevices.addEventListener !== "function"
  ) {
    return () => undefined;
  }
  navigator.mediaDevices.addEventListener("devicechange", handler);
  return () =>
    navigator.mediaDevices.removeEventListener("devicechange", handler);
}

/** Apply an output device to an <audio>/<video> element if supported.
 *  No-op on iOS Safari; the OS-level output device is followed instead. */
export async function applySinkId(
  el: HTMLMediaElement | null,
  sinkId: string | null | undefined,
): Promise<void> {
  if (!el || !sinkId) return;
  if (!supportsSinkId()) return;
  // setSinkId is still behind a conditional type on some browsers.
  type WithSink = HTMLMediaElement & {
    setSinkId?: (id: string) => Promise<void>;
  };
  const sink = (el as WithSink).setSinkId;
  if (typeof sink !== "function") return;
  try {
    await sink.call(el, sinkId);
  } catch (err) {
    console.warn("[devices] setSinkId failed", err);
  }
}
